import {
  Coins,
  MapPin,
  ArrowUpDown,
  SlidersHorizontal,
  LayoutGrid,
  List,
  HelpCircle,
  ChevronDown,
  Settings,
  Eye,
  EyeOff,
  Rows3,
} from 'lucide-react';
import { motion } from 'motion/react';
import { useRef, useEffect } from 'react';
import { CoinsBalance } from './CoinsBalance';
import { StandardTooltip } from './StandardTooltip';
import { ViewModeToggle } from './ViewModeToggle';
import { OnboardingStepper, OnboardingStep } from './OnboardingStepper';
import { IUser } from '@/types/user';

interface TopBarProps {
  onOpenFilter: (type: 'location' | 'sort' | 'filters' | 'categories') => void;
  coinsBalance: number;
  onOpenBuyCoins?: () => void;
  viewMode?: 'cards' | 'list';
  onViewModeChange?: (mode: 'cards' | 'list') => void;
  sortOption?: string;
  isFiltersActive?: boolean;
  isCategoriesActive?: boolean;
  onboardingStep?: OnboardingStep;
  onOnboardingAction?: (step: OnboardingStep) => void;
  ordersDisabled?: boolean;
  activeFiltersCount?: number;
  selectedCategoriesCount?: number;
  isMenuExpanded?: boolean;
  onMenuExpandedChange?: (expanded: boolean) => void;
  onHeightChange?: (height: number) => void;
  userInfo: IUser;
}

export function TopBar({
  onOpenFilter,
  coinsBalance,
  onOpenBuyCoins,
  viewMode,
  onViewModeChange,
  sortOption = 'newest',
  isFiltersActive = false,
  isCategoriesActive = false,
  onboardingStep = 4,
  onOnboardingAction,
  ordersDisabled = false,
  activeFiltersCount = 0,
  selectedCategoriesCount = 0,
  isMenuExpanded = false,
  onMenuExpandedChange,
  onHeightChange,
  userInfo,
}: TopBarProps) {
  const topBarRef = useRef<HTMLDivElement>(null);

  // Measure and report height changes
  useEffect(() => {
    const measureHeight = () => {
      if (topBarRef.current && onHeightChange) {
        const height = topBarRef.current.getBoundingClientRect().height;
        onHeightChange(height);
      }
    };

    // Measure immediately
    measureHeight();

    // Measure after a short delay to account for animations
    const timer = setTimeout(measureHeight, 100);

    // Use ResizeObserver to track height changes
    const resizeObserver = new ResizeObserver(measureHeight);
    if (topBarRef.current) {
      resizeObserver.observe(topBarRef.current);
    }

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, [onboardingStep, viewMode, isMenuExpanded, onHeightChange]);

  // Check if stepper is active (steps 0, 1, 2, or 3)
  const isStepperActive = onboardingStep !== 4;

  const userLocation = `${userInfo?.profile?.address?.street} • ${userInfo?.profile?.address?.neighborhood}, ${userInfo?.profile?.address?.uf} `;

  return (
    <>
      <div
        ref={topBarRef}
        className="z-40"
        style={{
          position: 'fixed',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          backgroundColor: isStepperActive ? 'transparent' : 'white',
          borderBottom: isStepperActive ? 'none' : '1px solid #E2E8F0',
        }}
      >
        {/* Header with Title + Coins Balance */}
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{
            background: isStepperActive
              ? 'linear-gradient(135deg, var(--kojo-blue) 0%, var(--kojo-blue-dark) 100%)'
              : 'transparent',
            position: 'relative',
            padding: '0.8rem',
          }}
        >
          {/* Subtle overlay for depth (only when stepper is active) */}
          {isStepperActive && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  'radial-gradient(circle at top right, rgba(255, 255, 255, 0.1), transparent 60%)',
                pointerEvents: 'none',
              }}
            />
          )}

          {/*<StandardTooltip
            content="Aqui você encontra todos os pedidos disponíveis para liberar."
            position="bottom"
          >
            <motion.div
              className="flex items-center gap-2 cursor-pointer group"
              style={{ position: 'relative', zIndex: 1 }}
              whileTap={{ scale: 0.98 }}
            >
              <h2
                style={{
                  fontSize: 'var(--text-lg)',
                  lineHeight: '1.4',
                  fontWeight: 'var(--font-weight-medium)',
                  color: isStepperActive ? 'white' : 'var(--foreground)',
                  transition: 'color 0.3s ease',
                }}
              >
                Loja de Pedidos
              </h2>
              <HelpCircle
                className="w-4 h-4 transition-colors"
                style={{
                  color: isStepperActive
                    ? 'rgba(255, 255, 255, 0.75)'
                    : 'var(--muted-foreground)',
                }}
              />
            </motion.div>
          </StandardTooltip>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <CoinsBalance
              balance={coinsBalance}
              onClick={onOpenBuyCoins}
              onboardingStep={onboardingStep}
            />
          </div>*/}
        </div>

        {/* Onboarding Stepper */}
        {onboardingStep !== undefined && onOnboardingAction && (
          <OnboardingStepper
            currentStep={onboardingStep}
            onAction={onOnboardingAction}
          />
        )}

        {/* Expandable Menu Toggle Button - Hidden during Steps 0 & 1 */}
        {onboardingStep !== 0 && onboardingStep !== 1 && (
          <motion.div
            style={{
              backgroundColor: 'var(--background)',
              borderBottom: '1px solid var(--border)',
              position: 'relative',
              paddingBottom: '0.5rem',
            }}
            whileHover={
              ordersDisabled ? {} : { backgroundColor: 'var(--muted)' }
            }
            transition={{ duration: 0.15 }}
          >
            <motion.button
              onClick={() => {
                if (onMenuExpandedChange) {
                  onMenuExpandedChange(!isMenuExpanded);
                }
              }}
              className="w-full flex items-center justify-between px-4 py-3"
              style={{
                backgroundColor: 'transparent',
                cursor: ordersDisabled ? 'not-allowed' : 'pointer',
                border: 'none',
              }}
              whileTap={ordersDisabled ? {} : { scale: 0.99 }}
              disabled={ordersDisabled}
            >
              <div
                className="flex items-center gap-2"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <motion.div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    paddingTop: '16px',
                    paddingBottom: '8px',
                  }}
                  whileHover={ordersDisabled ? {} : { scale: 1.1 }}
                  transition={{ duration: 0.15 }}
                >
                  <SlidersHorizontal
                    className="w-2 h-2"
                    style={{
                      width: 16,
                      height: 16,
                      color: ordersDisabled
                        ? 'var(--muted-foreground)'
                        : 'var(--foreground)',
                    }}
                  />
                </motion.div>
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: ordersDisabled
                      ? 'var(--muted-foreground)'
                      : 'var(--foreground)',
                    paddingTop: '16px',
                    paddingBottom: '8px',
                    paddingLeft: '4px',
                  }}
                >
                  Configurações da loja
                </span>
              </div>
              <motion.div
                animate={{ rotate: isMenuExpanded ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  paddingTop: '16px',
                  paddingBottom: '8px',
                }}
              >
                <ChevronDown
                  style={{
                    width: 16,
                    height: 16,
                    color: ordersDisabled
                      ? 'var(--muted-foreground)'
                      : 'var(--muted-foreground)',
                  }}
                />
              </motion.div>
            </motion.button>
          </motion.div>
        )}

        {/* Filter Buttons - Expandable */}
        <motion.div
          initial={false}
          animate={{
            height: isMenuExpanded ? 'auto' : 0,
            opacity: isMenuExpanded ? 1 : 0,
          }}
          transition={{
            duration: 0.3,
            ease: [0.4, 0, 0.2, 1],
          }}
          style={{
            overflow: 'hidden',
          }}
        >
          <div
            className="flex flex-col"
            style={{
              backgroundColor: 'var(--background)',
              borderBottom: '1px solid var(--border)',
            }}
          >
            {/* Sort option */}
            <motion.button
              onClick={ordersDisabled ? undefined : () => onOpenFilter('sort')}
              className="flex items-center justify-between px-4 py-3 transition-colors"
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                cursor: ordersDisabled ? 'not-allowed' : 'pointer',
                opacity: ordersDisabled ? 0.5 : 1,
                borderBottom: '1px solid var(--border)',
                padding: '12px 16px',
              }}
              whileHover={
                ordersDisabled ? {} : { backgroundColor: 'var(--muted)' }
              }
              whileTap={ordersDisabled ? {} : { scale: 0.995 }}
            >
              <div className="flex items-center gap-3">
                <ArrowUpDown
                  className="w-4 h-4"
                  style={{
                    width: '16px',
                    height: '16px',
                    color:
                      sortOption !== 'recommended'
                        ? 'var(--kojo-blue)'
                        : 'var(--muted-foreground)',
                  }}
                />
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--foreground)',
                  }}
                >
                  Ordenar
                </span>
              </div>
              <span
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--kojo-blue)',
                  fontWeight: 'var(--font-weight-medium)',
                }}
              >
                {sortOption === 'newest'
                  ? 'Mais recente'
                  : sortOption === 'remote'
                    ? 'Pedidos remotos'
                    : sortOption === 'distance'
                      ? 'Mais próximos'
                      : sortOption === 'urgency'
                        ? 'Mais urgentes'
                        : ''}
              </span>
            </motion.button>

            {/* Filter option */}
            <motion.button
              onClick={
                ordersDisabled ? undefined : () => onOpenFilter('filters')
              }
              className="flex items-center justify-between px-4 py-3 transition-colors"
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                cursor: ordersDisabled ? 'not-allowed' : 'pointer',
                opacity: ordersDisabled ? 0.5 : 1,
                borderBottom: '1px solid var(--border)',
                padding: '12px 16px',
              }}
              whileHover={
                ordersDisabled ? {} : { backgroundColor: 'var(--muted)' }
              }
              whileTap={ordersDisabled ? {} : { scale: 0.995 }}
            >
              <div className="flex items-center gap-3">
                <Eye
                  className="w-4 h-4"
                  style={{
                    width: '16px',
                    height: '16px',
                    color: 'var(--foreground)',
                  }}
                />
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--foreground)',
                  }}
                >
                  Mostrar
                </span>
              </div>
              <div className="flex items-center gap-2">
                {isFiltersActive && (
                  <span
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--kojo-blue)',
                    }}
                  />
                )}
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    color: isFiltersActive
                      ? 'var(--kojo-blue)'
                      : 'var(--muted-foreground)',
                    fontWeight: 'var(--font-weight-medium)',
                  }}
                >
                  {isFiltersActive
                    ? activeFiltersCount === 1
                      ? '1 tipo oculto'
                      : `${activeFiltersCount} tipos ocultos`
                    : 'Nenhum'}
                </span>
              </div>
            </motion.button>

            {/* Location option */}
            <motion.button
              onClick={
                ordersDisabled ? undefined : () => onOpenFilter('location')
              }
              className="flex items-center justify-between px-4 py-3 transition-colors"
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                cursor: ordersDisabled ? 'not-allowed' : 'pointer',
                opacity: ordersDisabled ? 0.5 : 1,
                borderBottom: '1px solid var(--border)',
                padding: '12px 16px',
              }}
              whileHover={
                ordersDisabled ? {} : { backgroundColor: 'var(--muted)' }
              }
              whileTap={ordersDisabled ? {} : { scale: 0.995 }}
            >
              <div className="flex items-center gap-3">
                <MapPin
                  className="w-4 h-4"
                  style={{
                    width: '16px',
                    height: '16px',
                    color: 'var(--foreground)',
                  }}
                />
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--foreground)',
                  }}
                >
                  Localização
                </span>
              </div>
              <span
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--kojo-blue)',
                  fontWeight: 'var(--font-weight-medium)',
                }}
              >
                {userLocation}
              </span>
            </motion.button>

            {/* Categories option */}
            {/*<motion.button
              onClick={
                ordersDisabled ? undefined : () => onOpenFilter('categories')
              }
              className="flex items-center justify-between px-4 py-3 transition-colors"
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                cursor: ordersDisabled ? 'not-allowed' : 'pointer',
                opacity: ordersDisabled ? 0.5 : 1,
                borderBottom: '1px solid var(--border)',
                padding: '12px 16px',
              }}
              whileHover={
                ordersDisabled ? {} : { backgroundColor: 'var(--muted)' }
              }
              whileTap={ordersDisabled ? {} : { scale: 0.995 }}
            >
              <div className="flex items-center gap-3">
                <LayoutGrid
                  className="w-4 h-4"
                  style={{
                    width: '16px',
                    height: '16px',
                    color: 'var(--foreground)',
                  }}
                />
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--foreground)',
                  }}
                >
                  Categorias
                </span>
              </div>
              <div className="flex items-center gap-2">
                {isCategoriesActive && (
                  <span
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--kojo-blue)',
                    }}
                  />
                )}
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    color: isCategoriesActive
                      ? 'var(--kojo-blue)'
                      : 'var(--muted-foreground)',
                    fontWeight: 'var(--font-weight-medium)',
                  }}
                >
                  {isCategoriesActive
                    ? `${selectedCategoriesCount} selecionadas`
                    : 'Todas'}
                </span>
              </div>
            </motion.button>*/}

            {/* View mode toggle */}
            {/*{viewMode && onViewModeChange && (
              <div
                className="flex items-center justify-between px-4 py-3"
                style={{
                  borderBottom: '1px solid var(--border)',
                  padding: '12px 16px',
                }}
              >
                <div className="flex items-center gap-3">
                  <Rows3
                    className="w-4 h-4"
                    style={{
                      color: 'var(--foreground)',
                      width: '16px',
                      height: '16px',
                    }}
                  />
                  <span
                    style={{
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: 'var(--foreground)',
                    }}
                  >
                    Lista
                  </span>
                </div>
                <ViewModeToggle
                  viewMode={viewMode}
                  onViewModeChange={onViewModeChange}
                  disabled={ordersDisabled}
                  onClose={() =>
                    onMenuExpandedChange && onMenuExpandedChange(false)
                  }
                />
              </div>
            )}*/}
          </div>
        </motion.div>
      </div>
      {isMenuExpanded && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            zIndex: 30,
          }}
          onClick={() => onMenuExpandedChange && onMenuExpandedChange(false)}
        />
      )}
    </>
  );
}
